import { Button, message } from 'antd';
import { isEmpty } from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { AD_PLAYS } from '@utils/constants';
import { DIMENSION_LIMIT } from '@store/trends/base/reducer';
import { adContentMetricsSelector } from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { setDimensions, setGlobalOrderByMetric } from '@store/trends/base/actions';
import { dimensionsSelector, globalOrderBySelector } from '@store/trends/base/selectors';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import useDimensionsCategories from '../hooks/useDimensionsCategories';
import DimensionPopupModal from './DimensionPopupModal';
import { DIMENSION_POPUP_OK_TEXT, DIMENSION_POPUP_TITLE, hasAdDimensions } from './utils';

const isAdModule = window.conviva.module === 'AI';
const defaultAIMetric = AD_PLAYS;

const DimensionsSelectorPopup = ({ buttonText = 'Dimensions', excludeCategories = [] }) => {
  const dispatch = useDispatch();
  const allDimensions = useSelector(allDimensionsListSelector) as any[];
  const selectedDimensionsFromStore = useSelector(dimensionsSelector);
  const globalOrderBy = useSelector(globalOrderBySelector);
  const adContentMetrics = useSelector(adContentMetricsSelector);

  const [selectedDimensionsList, setSelectedDimensionsList] = useState(selectedDimensionsFromStore);
  const searchRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const { dataScope } = useSelector(activeDashboardSelector);

  const openPopup = () => {
    setVisible(true);
    // focus on search box after a delay
    // ref init + visual feedback after popup animation
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus({
          cursor: 'start',
        });
      }
    }, 500);
  };

  const closePopup = () => {
    setVisible(false);
    setSelectedDimensionsList(selectedDimensionsFromStore);
  };

  const onOk = () => {
    if (selectedDimensionsList && selectedDimensionsList.length > 0) {
      setVisible(false);
      dispatch(setDimensions(selectedDimensionsList));
      if (
        !isAdModule &&
        hasAdDimensions(selectedDimensionsList, dimensionsCategories) &&
        !(globalOrderBy in adContentMetrics)
      )
        dispatch(setGlobalOrderByMetric(defaultAIMetric));
    }
  };

  const onClear = () => {
    setSelectedDimensionsList([]);
  };

  const dimensionsCategories = useDimensionsCategories({
    allDimensions,
    selectedDimensionsList,
    excludeCategories,
    hiddenEmptyCategory: !!dataScope,
  });

  useEffect(() => {
    setSelectedDimensionsList(selectedDimensionsFromStore);
  }, [selectedDimensionsFromStore]);

  const onChange = (item) => {
    const dimensionKey = item.target.value;
    if (selectedDimensionsList.includes(dimensionKey)) {
      setSelectedDimensionsList(selectedDimensionsList.filter((value) => value !== dimensionKey));
    } else {
      setSelectedDimensionsList([...selectedDimensionsList, dimensionKey]);
    }
  };

  useEffect(() => {
    if (selectedDimensionsList.length > DIMENSION_LIMIT.max) {
      message.warning(`You can select a maximum of ${DIMENSION_LIMIT.max} dimensions.`);
      setSelectedDimensionsList(selectedDimensionsList.slice(0, DIMENSION_LIMIT.max)); // why?
    }
  }, [selectedDimensionsList]);

  if (isEmpty(allDimensions)) {
    return null;
  }

  return (
    <>
      <Button onClick={openPopup} icon={<PlusOutlined />}>
        {buttonText}
      </Button>
      <DimensionPopupModal
        title={DIMENSION_POPUP_TITLE}
        visible={visible}
        onCancel={closePopup}
        onOk={onOk}
        okText={DIMENSION_POPUP_OK_TEXT}
        dimensionsCategories={dimensionsCategories}
        selectedDimensionsList={selectedDimensionsList}
        onChange={onChange}
        onClear={onClear}
        searchRef={searchRef}
      />
    </>
  );
};

export default DimensionsSelectorPopup;
